import { Box, Button, Grid, Input, Modal, IconButton, Snackbar, Alert } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import React from 'react';
import { FixedSizeList as List } from 'react-window';
import { LateSwapLineup, LateSwapPlayer } from './store/types';
import { StackExposures } from '../preContest/queries/queryTypes';
import RoiBoostButton from '../common/Table/RoiBoostButton';
import { useLocalStorage } from 'usehooks-ts';
import { findLineupsToFavorite } from './utils';
import { SlateResponse } from '../common/store/types';
import { GridApiPro } from '@mui/x-data-grid-pro/models/gridApiPro';
import { gridFilteredSortedRowEntriesSelector } from '@mui/x-data-grid-pro';

// Reusable Components
type SortDirection = 'asc' | 'desc' | null;

const HeaderCell = ({
  children,
  center = false,
  sortable = false,
  sortDirection = null,
  onSort,
}: {
  children: React.ReactNode;
  center?: boolean;
  sortable?: boolean;
  sortDirection?: SortDirection;
  onSort?: () => void;
}) => (
  <Box
    onClick={sortable ? onSort : undefined}
    sx={{
      fontSize: 11,
      fontWeight: 700,
      color: 'rgba(255, 255, 255, 0.70)',
      textTransform: 'uppercase',
      padding: '0 8px',
      textAlign: center ? 'center' : 'left',
      cursor: sortable ? 'pointer' : 'default',
      display: 'flex',
      alignItems: 'center',
      justifyContent: center ? 'center' : 'flex-start',
      gap: 0.5,
      '&:hover': sortable ? {
        color: 'rgba(255, 255, 255, 0.90)',
      } : {},
    }}
  >
    {children}
    {sortable && sortDirection === 'asc' && <ArrowUpwardIcon sx={{ fontSize: 14 }} />}
    {sortable && sortDirection === 'desc' && <ArrowDownwardIcon sx={{ fontSize: 14 }} />}
  </Box>
);

const DataCell = ({ children, center = false, bold = false }: { children: React.ReactNode; center?: boolean; bold?: boolean }) => (
  <Box sx={{
    display: 'flex',
    alignItems: 'center',
    justifyContent: center ? 'center' : 'flex-start',
    fontSize: 13,
    padding: '0 4px',
    fontWeight: bold ? 700 : 400,
    height: '100%',
  }}>
    {children}
  </Box>
);

interface OwnProps {
  readonly open: boolean;
  readonly stacks: StackExposures[];
  readonly stackRoiList: { [key: string]: number };
  readonly stackExposureList: { [key: string]: number };
  readonly setStackRoiList: (list: { [key: string]: number }) => void;
  readonly setStackExposureList: (list: { [key: string]: number }) => void;
  readonly closeModal: () => void;
  readonly selectedSlate: SlateResponse;
  readonly lineups: LateSwapLineup[];
  readonly players: LateSwapPlayer[];
  readonly contestName: string;
  readonly allowDupes: boolean;
  readonly playerExposureDict: { [key: string]: number };
  readonly playerRoiList: { [key: string]: number };
  readonly apiRef?: React.MutableRefObject<GridApiPro> | null;
}

const LateSwapStackManagementModal = (props: OwnProps) => {
  const {
    open,
    stacks,
    stackRoiList,
    stackExposureList,
    setStackRoiList,
    setStackExposureList,
    closeModal,
    selectedSlate,
    lineups,
    players,
    contestName,
    allowDupes,
    playerExposureDict,
    playerRoiList,
    apiRef,
  } = props;

  const [updatedRoiList, setUpdatedRoiList] = React.useState<{ [key: string]: number }>(stackRoiList);
  const [updatedExposureList, setUpdatedExposureList] = React.useState<{ [key: string]: number }>(stackExposureList);
  const [filteredStacks, setFilteredStacks] = React.useState<StackExposures[]>([]);
  const [searchVal, setSearchVal] = React.useState('');
  const [globalMaxExposure, setGlobalMaxExposure] = React.useState<number>(100);
  const [snackbarOpen, setSnackbarOpen] = React.useState(false);
  const [sortColumn, setSortColumn] = useLocalStorage<string>('lateswap-stack-modal-sort-column', 'exposure');
  const [sortDirection, setSortDirection] = useLocalStorage<SortDirection>('lateswap-stack-modal-sort-direction', 'desc');

  const [savedLineups, setSavedLineups] = useLocalStorage<LateSwapLineup[]>(
    "lateswap-lineups",
    []
  );

  const sport = selectedSlate?.sport?.toLowerCase();

  // Calculate current stack exposure based on savedLineups
  const computedStackExposures = React.useMemo(() => {
    const exposureMap: { [key: string]: number } = {};
    if (savedLineups.length === 0) return exposureMap;

    savedLineups.forEach(lineup => {
      if (!lineup?.stacks) return;
      lineup.stacks.forEach(stack => {
        // Only count stacks that meet the threshold (3+ for MLB, 1+ for NFL)
        if ((sport === 'mlb' && stack.count > 2) || (sport === 'nfl' && stack.count > 0)) {
          exposureMap[stack.team] = (exposureMap[stack.team] || 0) + 1;
        }
      });
    });

    // Convert counts to percentages
    const totalLineups = savedLineups.length;
    Object.keys(exposureMap).forEach(key => {
      exposureMap[key] = (exposureMap[key] / totalLineups) * 100;
    });

    return exposureMap;
  }, [savedLineups, sport]);

  // Get computed exposure for a stack
  const getComputedStackExposure = React.useCallback((team: string) => {
    return computedStackExposures[team] || 0;
  }, [computedStackExposures]);

  const getTotalExposureValue = React.useCallback((stack: StackExposures) => {
    // Use computed exposure from savedLineups instead of stale prop data
    // Return as decimal (0-1) since getTotalExposure multiplies by 100
    return getComputedStackExposure(stack.team) / 100;
  }, [getComputedStackExposure]);

  const sortStacks = React.useCallback((stackList: StackExposures[]) => {
    const multiplier = sortDirection === 'asc' ? 1 : -1;
    return [...stackList].sort((a, b) => {
      switch (sortColumn) {
        case 'team':
          return multiplier * (a.team || '').localeCompare(b.team || '');
        case 'exposure':
          return multiplier * (getTotalExposureValue(a) - getTotalExposureValue(b));
        case 'roiBoost':
          return multiplier * ((updatedRoiList[a.team] || 0) - (updatedRoiList[b.team] || 0));
        case 'exposureCap':
          return multiplier * ((updatedExposureList[a.team] ?? 100) - (updatedExposureList[b.team] ?? 100));
        default:
          return multiplier * (getTotalExposureValue(a) - getTotalExposureValue(b));
      }
    });
  }, [sortColumn, sortDirection, updatedRoiList, updatedExposureList, getTotalExposureValue]);

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('desc');
    }
  };

  React.useEffect(() => {
    setFilteredStacks(sortStacks(stacks));
  }, [stacks, sortStacks]);

  React.useEffect(() => {
    setUpdatedRoiList(stackRoiList);
  }, [stackRoiList]);

  React.useEffect(() => {
    setUpdatedExposureList(stackExposureList);
  }, [stackExposureList]);

  React.useEffect(() => {
    let result = sortStacks(stacks);
    if (searchVal) {
      result = result.filter(x => x.team?.toLowerCase().includes(searchVal.toLowerCase()));
    }
    setFilteredStacks(result);
  }, [searchVal, stacks, sortStacks]);

  const handleRoiChange = React.useCallback((team: string, percent: number) => {
    setUpdatedRoiList(prev => ({ ...prev, [team]: percent }));
  }, []);

  const handleExposureChange = React.useCallback((team: string, percent: number) => {
    setUpdatedExposureList(prev => ({ ...prev, [team]: percent }));
  }, []);

  const handleGlobalMaxExposureChange = (percent: number) => {
    setGlobalMaxExposure(percent);
    const newExposureList: { [key: string]: number } = {};
    stacks.forEach(stack => {
      newExposureList[stack.team] = percent;
    });
    setUpdatedExposureList(newExposureList);
  };

  const getRoiPercent = React.useCallback((team: string) => {
    return updatedRoiList[team] !== undefined ? updatedRoiList[team] : 0;
  }, [updatedRoiList]);

  const getExposurePercent = React.useCallback((team: string) => {
    return updatedExposureList[team] !== undefined ? updatedExposureList[team] : 100;
  }, [updatedExposureList]);

  const getTotalExposure = (stack: StackExposures) => {
    return (getTotalExposureValue(stack) * 100).toFixed(2) + '%';
  };

  const handleClearExposures = () => setUpdatedExposureList({});
  const handleClearBoosts = () => setUpdatedRoiList({});

  const handleAdjustFavorites = () => {
    const filteredRoiList: { [key: string]: number } = {};
    const filteredExposureList: { [key: string]: number } = {};

    Object.keys(updatedRoiList).forEach(key => {
      if (updatedRoiList[key] !== 0) {
        filteredRoiList[key] = updatedRoiList[key];
      }
    });

    Object.keys(updatedExposureList).forEach(key => {
      if (updatedExposureList[key] !== 100 && updatedExposureList[key] !== undefined) {
        filteredExposureList[key] = updatedExposureList[key];
      }
    });

    setStackRoiList(filteredRoiList);
    setStackExposureList(filteredExposureList);

    // Refavorite lineups when we have saved lineups
    // Don't clear savedLineups first to avoid visual flash
    if (savedLineups.length > 0) {
      setTimeout(() => {
        // Get filtered lineups from the grid if filters are applied
        let lineupsToUse = lineups;
        try {
          if (apiRef?.current) {
            const filteredRows = gridFilteredSortedRowEntriesSelector(apiRef);
            // Check if the grid has fewer rows than total lineups (filters applied)
            if (filteredRows.length > 0 && filteredRows.length < lineups.length) {
              // Create a set of visible lineup IDs for quick lookup
              const visibleLineupIds = new Set(filteredRows.map(row => row.model?.id));
              lineupsToUse = lineups.filter(l => visibleLineupIds.has(l.id));
            }
          }
        } catch (e) {
          // Grid not available or error - proceed without filtering
        }

        const result = findLineupsToFavorite(
          lineupsToUse,
          players,
          [],
          playerExposureDict,
          filteredExposureList,
          playerRoiList,
          [],
          contestName,
          selectedSlate,
          allowDupes,
          filteredRoiList
        );
        setSavedLineups(result.lineups as LateSwapLineup[]);
        setSnackbarOpen(true);
        // Re-sort to reflect updated exposures
        setFilteredStacks(sortStacks(stacks));
      }, 100);
    } else {
      setSnackbarOpen(true);
    }
  };

  const ROW_HEIGHT = 64;
  const LIST_HEIGHT = 300;

  // Memoized row renderer for virtualization
  const StackRow = React.memo(({ index, style }: { index: number; style: React.CSSProperties }) => {
    const stack = filteredStacks[index];
    if (!stack) return null;

    return (
      <Box
        style={style}
        sx={{
          display: 'flex',
          alignItems: 'center',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.02)' },
        }}
      >
        <Grid container alignItems="center" sx={{ height: '100%' }}>
          <Grid item xs={3}>
            <DataCell>
              <Box sx={{ fontSize: 14, fontWeight: 700, textTransform: 'uppercase' }}>
                {stack.team}
                {stack.QbName && (
                  <Box component="span" sx={{ fontWeight: 400, fontSize: 12, color: 'rgba(255, 255, 255, 0.7)', ml: 1 }}>
                    {stack.QbName}
                  </Box>
                )}
              </Box>
            </DataCell>
          </Grid>
          <Grid item xs={3}><DataCell center bold>{getTotalExposure(stack)}</DataCell></Grid>
          <Grid item xs={3}>
            <DataCell center>
              <RoiBoostButton
                defaultPercent={0}
                onChange={(percent) => handleRoiChange(stack.team, percent)}
                previousPercent={getRoiPercent(stack.team)}
              />
            </DataCell>
          </Grid>
          <Grid item xs={3}>
            <DataCell center>
              <RoiBoostButton
                defaultPercent={100}
                onChange={(percent) => handleExposureChange(stack.team, percent)}
                previousPercent={getExposurePercent(stack.team)}
                constantMax={100}
                constantMin={0}
              />
            </DataCell>
          </Grid>
        </Grid>
      </Box>
    );
  });

  return (
    <>
      <Modal
        open={open}
        disableAutoFocus
        onClose={(_event: object, reason: string) => {
          if (reason?.toLowerCase() === 'backdropclick') closeModal();
        }}
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          color: 'white',
          fontFamily: 'Roboto',
          backgroundColor: 'rgba(125, 125, 125, .65)',
        }}
      >
        <Box sx={{
          backgroundColor: '#0A0A0A',
          borderRadius: 2,
          minHeight: 400,
          width: 900,
          overflow: 'hidden',
          border: '1px solid rgba(255, 255, 255, .30)',
        }}>
          {/* Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '20px 20px 0 20px' }}>
            <Box>
              <Box sx={{ fontSize: 20, fontWeight: 600, marginBottom: 1 }}>Stack Management</Box>
              <Box sx={{ fontSize: 14, fontWeight: 400, color: 'rgba(255, 255, 255, 0.7)', maxWidth: 550, lineHeight: 1.4 }}>
                Add positive or negative ROI boosts to lineups that include this stack to influence ROI rankings.
                You can also set exposure caps to limit how often the stack appears.
              </Box>
            </Box>
            <IconButton onClick={closeModal} sx={{ color: 'white' }}>
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Search */}
          <Box sx={{ margin: '16px 20px', backgroundColor: 'rgba(43, 48, 62, 0.15)', border: '1px solid #2C3140', borderRadius: 1 }}>
            <Input
              value={searchVal}
              onChange={(e) => setSearchVal(e.target.value)}
              placeholder="Search Teams"
              disableUnderline
              endAdornment={<SearchIcon style={{ color: 'rgba(255,255,255,0.5)' }} />}
              sx={{ color: 'rgba(255, 255, 255, 0.70)', width: '100%', padding: '8px 12px' }}
            />
          </Box>

          {/* Table Header */}
          <Box sx={{ backgroundColor: 'rgba(219, 77, 0, 0.15)', borderBottom: '1px solid rgba(219, 77, 0, .8)', padding: '8px 0', margin: '0 20px' }}>
            <Grid container alignItems="center">
              <Grid item xs={3}>
                <HeaderCell sortable sortDirection={sortColumn === 'team' ? sortDirection : null} onSort={() => handleSort('team')}>Team</HeaderCell>
              </Grid>
              <Grid item xs={3}>
                <HeaderCell center sortable sortDirection={sortColumn === 'exposure' ? sortDirection : null} onSort={() => handleSort('exposure')}>Current Exposure</HeaderCell>
              </Grid>
              <Grid item xs={3}>
                <HeaderCell center sortable sortDirection={sortColumn === 'roiBoost' ? sortDirection : null} onSort={() => handleSort('roiBoost')}>ROI Boost</HeaderCell>
              </Grid>
              <Grid item xs={3}>
                <HeaderCell center sortable sortDirection={sortColumn === 'exposureCap' ? sortDirection : null} onSort={() => handleSort('exposureCap')}>Exposure Cap</HeaderCell>
              </Grid>
            </Grid>
          </Box>

          {/* Virtualized Table Body */}
          <Box sx={{ margin: '0 20px', overflow: 'hidden' }}>
            <List
              height={LIST_HEIGHT}
              itemCount={filteredStacks.length}
              itemSize={ROW_HEIGHT}
              width="100%"
              style={{
                scrollbarWidth: 'thin',
                scrollbarColor: 'slategrey transparent',
                overflowX: 'hidden',
              }}
            >
              {StackRow}
            </List>
          </Box>

          {/* Footer */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 2, padding: '16px 20px', borderTop: '1px solid rgba(255, 255, 255, 0.1)', backgroundColor: '#0A0A0A' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: 14 }}>
              <span>Global Max Exposure:</span>
              <RoiBoostButton
                defaultPercent={100}
                onChange={handleGlobalMaxExposureChange}
                previousPercent={globalMaxExposure}
                constantMax={100}
                constantMin={0}
              />
            </Box>
            <Button
              variant="outlined"
              onClick={handleClearExposures}
              sx={{ color: 'white', borderColor: 'rgba(255, 255, 255, 0.3)', '&:hover': { borderColor: 'rgba(255, 255, 255, 0.5)', backgroundColor: 'rgba(255, 255, 255, 0.05)' } }}
            >
              CLEAR EXPOSURES
            </Button>
            <Button
              variant="outlined"
              onClick={handleClearBoosts}
              sx={{ color: 'white', borderColor: 'rgba(255, 255, 255, 0.3)', '&:hover': { borderColor: 'rgba(255, 255, 255, 0.5)', backgroundColor: 'rgba(255, 255, 255, 0.05)' } }}
            >
              CLEAR BOOSTS
            </Button>
            <Button
              variant="contained"
              endIcon={<OpenInNewIcon />}
              onClick={handleAdjustFavorites}
              sx={{ backgroundColor: 'rgba(219, 77, 0, 0.70)', color: 'white', '&:hover': { backgroundColor: 'rgba(219, 77, 0, 0.90)' } }}
            >
              ADJUST FAVORITES
            </Button>
          </Box>
        </Box>
      </Modal>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbarOpen(false)} severity="success" sx={{ width: '100%' }}>
          Favorites updated successfully!
        </Alert>
      </Snackbar>
    </>
  );
};

export default LateSwapStackManagementModal;
